import {
  Badge,
  Box,
  Card,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogProps,
  Table,
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
  theme,
} from "@aksara-ui/react";
import TableBodyRowsSkeleton from "components/TableBodyRowSkeleton";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { InputSearchbox } from "components/Form/InputSearchbox";
import { Pagination, PaginationFilter } from "components/Pagination";
import moment from "moment";
import { getAllCatalogGiftDetails } from "redux/actions";
import { IconCircle } from "@aksara-ui/icons";
import { toCapitilizeCase } from "utilities/helper/toCapitalizeCase";

interface Props {
  checkoutId: string;
  type: string;
}

const CatalogGiftDetailsContent: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  checkoutId,
  type,
}) => {
  const dispatch = useDispatch();

  const { isLoadingModal } = useSelector((state: any) => state.globalReducer);
  const { catalogGiftDetails } = useSelector(
    (state: any) => state.catalogGiftDetailReducer
  );
  const { currentPage, totalPage, limitPage } = useSelector(
    (state: any) => state.catalogGiftDetailReducer
  );

  const [searchValue, setSearchValue] = useState("");
  const [limit, setLimit] = useState(10);

  const handleSearchChange = (event: any) => {
    setSearchValue(event.target.value);
    dispatch(
      getAllCatalogGiftDetails(
        checkoutId,
        1,
        event.target.value,
        limitPage
      ) as any
    );
  };

  const handleClearSearchChange = () => {
    setSearchValue("");
    dispatch(
      getAllCatalogGiftDetails(checkoutId, currentPage, "", limitPage) as any
    );
  };

  const handleCloseModal = () => {
    onClose && onClose();
  };

  useEffect(() => {
    if (isOpen) {
      dispatch(getAllCatalogGiftDetails(checkoutId) as any);
    }
  }, [isOpen, dispatch, checkoutId]);

  return (
    <Dialog
      disableOverlayClick
      isOpen={isOpen}
      onClose={() => handleCloseModal()}
      overflowY={"auto"}
      maxHeight={"90vh"}
      minWidth={750}
      width={"95vw"}
      style={{
        height: "80vh",
        display: "flex",
        alignContent: "center",
      }}
    >
      <DialogHeader>
        <Text fontWeight={700}>{type} Gift Detail</Text>
      </DialogHeader>
      <DialogContent>
        <Box
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"space-between"}
          paddingBottom={25}
          style={{ gap: 20 }}
        >
          <InputSearchbox
            autoComplete="off"
            name="textDummy"
            placeholder="Search..."
            size={32}
            onChange={handleSearchChange}
            onClearButton={handleClearSearchChange}
          />
        </Box>

        <Card maxHeight={"100%"} overflow={"auto"} elevation={2}>
          <Table>
            <TableHead position={"sticky"} top={0} backgroundColor={"white"}>
              <TableHeadRow>
                {type === "Voucher" ? (
                  <TableHeadCell textAlign={"center"}>
                    Unique Code
                  </TableHeadCell>
                ) : (
                  <TableHeadCell textAlign={"center"}>
                    Tracking Number
                  </TableHeadCell>
                )}

                {type === "Voucher" ? (
                  <TableHeadCell textAlign={"center"} minWidth={150}>
                    Pin
                  </TableHeadCell>
                ) : (
                  <TableHeadCell textAlign={"center"} minWidth={150}>
                    Service
                  </TableHeadCell>
                )}
                {type === "Voucher" ? (
                  <TableHeadCell textAlign={"center"} minWidth={150}>
                    Expiry Date
                  </TableHeadCell>
                ) : (
                  <TableHeadCell textAlign={"center"} minWidth={150}>
                    Status
                  </TableHeadCell>
                )}
              </TableHeadRow>
            </TableHead>
            <TableBody>
              {isLoadingModal ? (
                <TableBodyRowsSkeleton rowsNumber={10} columnsNumber={3} />
              ) : !catalogGiftDetails.length ? (
                <TableBodyRow>
                  <TableBodyCell colSpan={3} textAlign={"center"}>
                    No stock available
                  </TableBodyCell>
                </TableBodyRow>
              ) : (
                catalogGiftDetails.map((gift: any) => {
                  return (
                    <TableBodyRow
                      key={gift.id}
                      backgroundColor={theme.colors.white}
                    >
                      <TableBodyCell whiteSpace={"nowrap"} textAlign={"center"}>
                        {type === "Voucher" ? (
                          <Badge
                            size="lg"
                            variant="info"
                            style={{
                              paddingLeft: 20,
                              paddingRight: 20,
                            }}
                          >
                            <Text fontWeight={700}>{gift.unique_code}</Text>
                          </Badge>
                        ) : gift.tracking_number ? (
                          <Badge
                            size="lg"
                            variant={"info"}
                            style={{
                              paddingLeft: 20,
                              paddingRight: 20,
                            }}
                          >
                            {gift.tracking_number}
                          </Badge>
                        ) : (
                          "-"
                        )}
                      </TableBodyCell>
                      <TableBodyCell textAlign={"center"}>
                        {type === "Voucher" ? (
                          <>{gift.pin ? "******" : "-"}</>
                        ) : (
                          <>{gift.service ? gift.service : "-"}</>
                        )}
                      </TableBodyCell>
                      <TableBodyCell textAlign={"center"}>
                        {type === "Voucher" ? (
                          moment(gift.expiry_date).format("DD MMM YYYY")
                        ) : (
                          <Badge
                            size="md"
                            variant={
                              gift.status === "pending"
                                ? "warning"
                                : gift.status === "in_transit"
                                ? "default"
                                : gift.status === "delivered"
                                ? "success"
                                : "critical"
                            }
                            style={{
                              color:
                                gift.status === "pending"
                                  ? "#F4B405"
                                  : gift.status === "in_transit"
                                  ? "#006FE6"
                                  : gift.status === "delivered"
                                  ? "#0C965A"
                                  : "#FF6150",
                            }}
                          >
                            <IconCircle
                              size={10}
                              fill={
                                gift.status === "pending"
                                  ? "#F4B405"
                                  : gift.status === "in_transit"
                                  ? "#006FE6"
                                  : gift.status === "delivered"
                                  ? "#0C965A"
                                  : "#FF6150"
                              }
                              style={{ marginRight: 5 }}
                            />
                            {gift.status === "in_transit"
                              ? "In Transit"
                              : toCapitilizeCase(gift.status)}
                          </Badge>
                        )}
                      </TableBodyCell>
                    </TableBodyRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Card>
        {type === "Voucher" ? (
          <Box
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"center"}
            paddingY={10}
            marginTop={10}
          >
            <Pagination
              current={currentPage}
              total={totalPage}
              onSelect={(select: any) =>
                dispatch(
                  getAllCatalogGiftDetails(
                    checkoutId,
                    select,
                    searchValue,
                    limitPage
                  ) as any
                )
              }
            />
            <PaginationFilter
              selectedItem={limit}
              onChange={(selectedItem) => {
                if (selectedItem) {
                  setLimit(selectedItem);
                  dispatch(
                    getAllCatalogGiftDetails(
                      checkoutId,
                      1,
                      searchValue,
                      selectedItem
                    ) as any
                  );
                }
              }}
              items={[10, 20, 30, 40, 50]}
            />
          </Box>
        ) : (
          <></>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CatalogGiftDetailsContent;
